import { useState, useEffect } from 'react';
import { ModalDialog, ButtonBar, FlowForm } from 't-lib-ui/structure';
import { TextButton, DropDownSelector, Input } from 't-lib-ui/widgets';
import { getAdminMagicSecret, setAdminMagicSecret } from '../../connector/auth';
import { usePutAdminCatalog } from '../../connector/api';
import { PostTextType } from '../../schema-copy/feed-schema';
import { textTypeOptions, toTextType } from './textTypeOptions';
import './AdminTextTypePopup.css';

interface AdminTextTypePopupProps {
  show: boolean;
  feedId: string;
  /** The catalog's current override; undefined = detected from the post stats. */
  textType?: PostTextType;
  /** What the post stats suggest without an override (undefined = no text badge). */
  detected?: string;
  onClose: () => void;
  /** Called with the new override (undefined = back to auto) after a successful save. */
  onChanged: (textType: PostTextType | undefined) => void;
}

/**
 * Small admin-only dialog for overriding a catalog entry's text badge ('full posts', 'summaries',
 * ...) from a feed tile, for feeds the post-stats detection gets wrong.
 */
const AdminTextTypePopup: React.FC<AdminTextTypePopupProps> = ({ show, feedId, textType, detected, onClose, onChanged }) => {
  const [selected, setSelected] = useState<string>(textType ?? '');
  const [secret, setSecret] = useState(getAdminMagicSecret() ?? '');
  // Only ask for the magic secret when it is missing, or after a request failed.
  const [askSecret, setAskSecret] = useState(!getAdminMagicSecret());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const magic = secret.trim();
  const putAdminCatalog = usePutAdminCatalog(feedId, magic);

  // Reset the form whenever the dialog (re)opens.
  useEffect(() => {
    if (!show) return;
    setSelected(textType ?? '');
    const stored = getAdminMagicSecret();
    setSecret(stored ?? '');
    setAskSecret(!stored);
    setSaving(false);
    setError(null);
  }, [show, feedId, textType]);

  const handleSave = async () => {
    if (saving || !magic) return;
    setSaving(true);
    setError(null);
    try {
      setAdminMagicSecret(magic);
      const newTextType = toTextType(selected);
      // null clears the override on the server.
      await putAdminCatalog({ textType: newTextType ?? null });
      onChanged(newTextType);
      onClose();
    } catch (e) {
      console.error('Failed to update text type:', e);
      setError(e instanceof Error ? e.message : 'Failed to update the content type.');
      setAskSecret(true);
      setSaving(false);
    }
  };

  return (
    <ModalDialog
      show={show}
      title="Change Content Type"
      closeButton="close"
      onClose={onClose}
      className="adminTextTypePopup"
      minWidth="440px"
    >
      <div style={{ padding: '0 20px 20px 20px' }}>
        <FlowForm>
          <DropDownSelector
            selections={textTypeOptions(detected)}
            value={selected}
            onSelect={v => setSelected(v.id)}
            focussable="flow"
            disabled={saving}
          />
          {askSecret && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: 4 }}>Admin secret</div>
              <Input
                type="password"
                value={secret}
                onInput={setSecret}
                placeholder="Admin secret"
                onEnter={handleSave}
                layout="singleCol"
              />
            </div>
          )}
          {error && <p style={{ color: 'var(--error-color)', fontSize: '0.85rem' }}>{error}</p>}
          <ButtonBar className="full-margin-top" align="right">
            <TextButton type="minor" onClick={onClose} label="Cancel" />
            <TextButton
              type="major"
              focussable="sideway"
              onClick={handleSave}
              label="Save"
              busy={saving}
              enabled={magic && !saving ? 'enabled' : 'disabled'}
            />
          </ButtonBar>
        </FlowForm>
      </div>
    </ModalDialog>
  );
};

export default AdminTextTypePopup;
